/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef } from 'react';
// @mui
import { Stack, Typography, Box, Container, Grid, Card, CardHeader, IconButton, Button } from '@mui/material';
import { Helmet } from 'react-helmet-async';
// components
import { useSelector, useDispatch } from 'react-redux';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import MultiSelector from '../multi-selector/multiSelector';
import VideoPlayer from '../video-player/videoPlayer';
import { appSliceActions } from '../../appSlices/appSlice';
import axiosInstance from '../../utils/axiosInstance';

export default function RecordedStreamTest() {
  const cameraList = useSelector((state) => state.app.cameraList);
  const testStatus = useSelector((state) => state.app.recordedTestFailed);
  const [selectedCameraList, setSelectedCameraList] = useState([]);
  const [cameraInRow, setCameraInRow] = useState(2);
  const [iteration, setIteration] = useState(0);
  const [startTest, setStartTest] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [startDate, setStartDate] = useState();
  const reasons = useRef([]);
  const [endDate, setEndDate] = useState();
  const notiRef = useRef(true);
  const testInterval = useRef();
  const dispatch = useDispatch();

  useEffect(() => {
    if (testStatus.status) {
      if (testInterval.current == null) {
        console.log('test has already stopped');
        dispatch(appSliceActions.setRecordedTestFailed({ status: false, reason: '' }));
        return;
      }
      const jsonData = {
        fallback: 'Auto test recorded stream',
        text: 'Auto test recorded stream',
        pretext: 'Recorded Stream',
        color: '#FF0000',
        fields: [
          {
            title: 'Iteration',
            value: iteration,
            short: false,
          },
          {
            title: 'Host',
            value: window.location.host,
            short: false,
          },
          {
            title: 'Reason',
            value: testStatus.reason,
            short: false,
          },
        ],
      };
      setEndDate(new Date());
      if (notiRef.current && testStatus.reason.includes('recorded')) {
        reasons.current.push(testStatus.reason);
        notiRef.current = false;
        axiosInstance
          .post(
            `null`,
            JSON.stringify(jsonData)
          )
          .then((response) => {
            console.log('slack noti', response.data);
          })
          .catch((e) => {
            console.error('slack noti error', e);
          });
        setTimeout(() => {
          notiRef.current = true;
        }, 5000);
      }
      handleStopTest();
      dispatch(appSliceActions.setRecordedTestFailed({ status: false, reason: '' }));
    }
  }, [testStatus, dispatch]);

  useEffect(() => {
    window.recordedStreamingCameras = selectedCameraList;
    return () => {
      window.recordedStreamingCameras = null;
      clearInterval(testInterval.current);
      testInterval.current = null;
    };
  }, [selectedCameraList]);

  useEffect(() => {
    setSelectedCameraList([]);
  }, [cameraList]);

  const handleInputValue = (val) => {
    setInputValue(val);
  };
  const handleSelectedCameraListChange = (val) => {
    setSelectedCameraList(val);
  };

  const handleZoomOut = () => {
    setCameraInRow((prev) => (prev < 3 ? prev + 1 : prev));
  };

  const handleZoomIn = () => {
    setCameraInRow((prev) => (prev > 1 ? prev - 1 : prev));
  };

  const closeCamera = (camera) => {
    const temp = selectedCameraList.filter((item) => item.sensorId !== camera.sensorId);
    setSelectedCameraList(temp);
  };

  const handleStartTest = () => {
    reasons.current = [];
    setStartTest(true);
    setIteration(1);
    setEndDate(null);
    setStartDate(new Date());
    testInterval.current = setInterval(() => {
      setStartTest(false);
      setTimeout(() => {
        if (!(testStatus.status || testInterval.current == null)) {
          setIteration((prev) => prev + 1);
          setStartTest(true);
        }
      }, 500);
    }, 110000);
  };

  const handleStopTest = () => {
    setStartTest(false);
    clearInterval(testInterval.current);
    testInterval.current = null;
  };

  return (
    <div>
      <Helmet>
        <title> stream </title>
      </Helmet>

      <Container maxWidth="xl">
        <Typography variant="h4" sx={{ mb: 5 }}>
          Recorded stream test
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <Box sx={{ p: 3 }}>
                <Typography>{`Iteration: ${iteration}`}</Typography>
                <Typography>{`Approx runtime: ${iteration * 40} seconds`}</Typography>
                {startDate != null && <Typography>{`Start date-time: ${startDate.toLocaleString()}`}</Typography>}
                {endDate != null && <Typography>{`End date-time: ${endDate.toLocaleString()}`}</Typography>}
                {reasons.current.map((el, index) => (
                  <Typography key={index}>{`Reason: ${el}`}</Typography>
                ))}
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card>
              <CardHeader title={'Recorded streams'} subheader={'Stream recorded streams'} />
              <Box sx={{ p: 3 }}>
                <Stack spacing={3} sx={{ mb: 2 }}>
                  <MultiSelector
                    id="recorded-stream-selector-multi"
                    placeholder="Type to search"
                    label="Select cameras"
                    value={selectedCameraList}
                    inputValue={inputValue}
                    onInputChange={handleInputValue}
                    onChange={handleSelectedCameraListChange}
                    getOptionLabel={(option) => {
                      if (option.remoteDeviceName && option.remoteDeviceLocation) {
                        return `${option.name} at ${option.remoteDeviceName} (${option.remoteDeviceLocation})`;
                      } if (option.remoteDeviceName) {
                        return `${option.name} at ${option.remoteDeviceName}`;
                      }
                      return option.name;

                    }}
                    options={cameraList}
                    disabled={startTest}
                  />
                </Stack>
                <Box
                  m={1}
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="flex-end"
                  sx={{ display: { xs: 'none', sm: 'none', md: 'none', lg: 'flex' } }}
                >
                  <IconButton onClick={handleZoomIn}>
                    <ZoomInIcon />
                  </IconButton>
                  <IconButton onClick={handleZoomOut}>
                    <ZoomOutIcon />
                  </IconButton>
                </Box>
              </Box>
              <Box m={2} display="flex" justifyContent="flex-end" alignItems="flex-end">
                <Button variant="contained" onClick={handleStartTest} sx={{ mr: 1 }}>
                  Start
                </Button>
                <Button variant="contained" onClick={handleStopTest}>
                  Stop
                </Button>
              </Box>
            </Card>
          </Grid>
          {startTest &&
            selectedCameraList.map((camera) => (
              <Grid item xs={12} sm={12} md={12 / cameraInRow} key={camera.sensorId}>
                <VideoPlayer
                  camera={camera}
                  cameraInRow={cameraInRow}
                  page="recorded"
                  closeCamera={closeCamera}
                  recordingTest
                />
              </Grid>
            ))}
        </Grid>
      </Container>
    </div>
  );
}
